import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import CityAutocomplete from '../components/CityAutocomplete';
import { api } from '../apiConfig';
import { getBuyerDisplayPrice } from '../utils/pricing';
import '../styles/Marketplace.css';

const DEFAULT_ITEMS_PER_ROW = 4;
const DEFAULT_PER_ARTIST_CAP = 6;
const DEFAULT_PAGE_SIZE = 24;
const MIN_FEATURED_PRICE = 200;
const PREFS_STORAGE_KEY = 'collectorPrefs';
const PLACEHOLDER_IMG = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600"><rect width="600" height="600" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23999" font-family="Arial, Helvetica, sans-serif" font-size="24">No Image</text></svg>';

const parseNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeProduct = (raw) => {
  const tags = Array.isArray(raw?.tags)
    ? raw.tags
    : typeof raw?.tags === 'string'
      ? raw.tags.split(',').map(t => t.trim()).filter(Boolean)
      : [];
  const image = raw?.image || raw?.image_url || raw?.imageUrl || raw?.images?.[0]?.src || raw?.images?.edges?.[0]?.node?.url || null;
  const priceString = raw?.price || raw?.priceRange?.minVariantPrice?.amount || raw?.variants?.[0]?.price || null;
  const price = parseNumber(priceString, null);
  const productUrl = raw?.url || raw?.product_url || raw?.productUrl || raw?.permalink || null;
  const handleFromUrl = (() => {
    const urlStr = productUrl;
    if (typeof urlStr === 'string') {
      const match = urlStr.match(/\/products\/([^\/?#]+)/i);
      if (match && match[1]) return decodeURIComponent(match[1]);
    }
    return null;
  })();
  const editionFromTags = (() => {
    const lower = tags.map(t => t.toLowerCase());
    if (lower.includes('original')) return 'original';
    if (lower.includes('limited') || lower.includes('limited edition')) return 'limited';
    if (lower.includes('open') || lower.includes('open edition')) return 'open';
    if (lower.includes('artist proof') || lower.includes('ap') || lower.includes('artist_proof')) return 'artist_proof';
    if (lower.includes('reproduction') || lower.includes('print')) return 'reproduction';
    return undefined;
  })();
  const dimensions = raw?.dimensions || raw?._raw?.dimensions || null;
  const yearCreated = raw?.year_created || raw?.year || raw?.yearCreated || null;
  // Extract medium from various possible sources - don't fall back to product_type
  const mediumMetafield = raw?.medium || 
    raw?._raw?.medium || 
    raw?.metafields?.['shopify.painting-medium'] || 
    raw?.metafields?.['custom.medium'] ||
    raw?.metafields?.medium ||
    raw?.painting_medium ||
    null;
  
  // Extract medium from tags if not found in metafields (e.g., "medium:oil", "Oil Painting")
  const mediumFromTags = (() => {
    if (mediumMetafield) return null; // Already have medium
    const mediumKeywords = ['oil', 'acrylic', 'watercolor', 'mixed media', 'mixed_media', 'digital', 
      'charcoal', 'ink', 'pastel', 'gouache', 'tempera', 'encaustic', 'collage', 'graphite', 
      'photography', 'sculpture', 'print', 'spray paint', 'enamel', 'resin'];
    for (const tag of tags) {
      const lower = tag.toLowerCase();
      // Check for "medium:value" format
      if (lower.startsWith('medium:')) return tag.split(':')[1].trim();
      // Check if tag matches a known medium
      for (const m of mediumKeywords) {
        if (lower === m || lower.includes(m + ' painting') || lower.includes(m + ' on ')) {
          return m.charAt(0).toUpperCase() + m.slice(1).replace(/_/g, ' ');
        }
      }
    }
    return null;
  })();
  
  const productType = raw?.type || raw?.product_type || raw?.productType || null;
  
  return {
    id: raw?.id,
    handle: raw?.handle || raw?.shopify_handle || raw?.product_handle || raw?.shopifyHandle || raw?.product?.handle || handleFromUrl || null,
    title: raw?.title,
    vendor: raw?.vendor || raw?.artist || raw?.author_name || null,
    medium: mediumMetafield || mediumFromTags || null, // Actual medium (oil, acrylic, etc.)
    productType, // Shopify product type (Artwork, Sculpture, etc.) - kept separate
    tags,
    image,
    price,
    estimatedShippingPrice: raw?.estimated_shipping_price || raw?.estimatedShippingPrice || raw?.shipping_estimate || null,
    shippingEstimateTier: raw?.shipping_estimate_tier || raw?.shippingEstimateTier || null,
    priceIncludesBuyerFees: Boolean(raw?.price_includes_buyer_fees || raw?.priceIncludesBuyerFees),
    editionType: raw?.edition_type || editionFromTags,
    dimensions,
    yearCreated,
    productUrl,
    isSold: Boolean(raw?.is_sold || raw?._raw?.is_sold),
    _raw: raw
  };
};

const formatDimensions = (dim) => {
  if (!dim) return '';
  if (typeof dim === 'string') return dim;
  try {
    const width = dim.width || dim.w || dim.W || null;
    const height = dim.height || dim.h || dim.H || null;
    const depth = dim.depth || dim.d || dim.D || null;
    const unit = dim.unit || dim.units || dim.u || 'in';
    const parts = [];
    if (width != null && height != null) parts.push(`${width}×${height}`);
    if (depth != null) parts.push(`${depth}`);
    return parts.length > 0 ? `${parts.join('×')} ${unit}` : '';
  } catch (_) {
    return '';
  }
};

const normalizeSearchText = (value) => String(value || '').toLowerCase().trim();

const productMatchesSearch = (product, query) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;

  const terms = normalizedQuery.split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;

  const searchableText = normalizeSearchText([
    product.title,
    product.vendor,
    product.medium,
    product.productType,
    product.handle,
    product.editionType,
    product.yearCreated,
    formatDimensions(product.dimensions),
    ...(product.tags || [])
  ].filter(Boolean).join(' '));

  return terms.every(term => searchableText.includes(term));
};

const getShopifyProductUrl = (product) => {
  if (!product) return null;
  if (product.productUrl) return product.productUrl;
  if (product._raw?.url) return product._raw.url;
  if (product._raw?.product_url) return product._raw.product_url;
  if (product.handle) return `https://artemerge.org/products/${encodeURIComponent(product.handle)}`;
  return null;
};

const getMarketplaceBuyerPrice = (product) => {
  if (!product) return null;
  if (product.priceIncludesBuyerFees) return product.price;
  return getBuyerDisplayPrice(product.price, product.estimatedShippingPrice);
};

const formatMarketplaceBuyerPrice = (product) => {
  const buyerPrice = getMarketplaceBuyerPrice(product);
  if (buyerPrice === null || buyerPrice === undefined) return '';
  return `$${Math.round(buyerPrice).toLocaleString()}`;
};

const getPriceBucket = (product) => {
  const price = getMarketplaceBuyerPrice(product);
  if (price == null) return 'unknown';
  if (price < MIN_FEATURED_PRICE) return 'under_minimum';
  if (price < 1000) return 'accessible';
  if (price < 5000) return 'mid';
  if (price < 10000) return 'upper_mid';
  return 'premium';
};

const navigateToStorefrontProduct = (href) => (event) => {
  if (!href) return;
  // Force a full-page navigation out of the React app so product links cannot be
  // interpreted by the app router after filtering/search state changes.
  event.preventDefault();
  event.stopPropagation();
  window.top.location.href = href;
};

const computePreferenceScore = (product, prefs) => {
  if (!product) return 0;
  const { styles = [], mediums = [], cities = [], priceMin = null, priceMax = null, followedArtists = [] } = prefs || {};
  let score = 0;
  // Style/tag matches
  if (styles.length > 0 && product.tags.length > 0) {
    const styleMatches = product.tags.filter(t => styles.includes(t.toLowerCase()));
    score += styleMatches.length * 3;
  }
  // Medium match
  if (mediums.length > 0 && product.medium) {
    if (mediums.map(m => m.toLowerCase()).includes(String(product.medium).toLowerCase())) score += 5;
  }
  // City match (assume encoded in tags like city:nyc or simple city string)
  if (cities.length > 0 && product.tags.length > 0) {
    const lowerCities = cities.map(c => c.toLowerCase());
    const hasCity = product.tags.some(t => {
      const lt = t.toLowerCase();
      if (lt.startsWith('city:')) return lowerCities.includes(lt.replace('city:', ''));
      return lowerCities.includes(lt);
    });
    if (hasCity) score += 4;
  }
  // Price preference
  const buyerDisplayPrice = getMarketplaceBuyerPrice(product);
  if (buyerDisplayPrice != null) {
    if (buyerDisplayPrice >= MIN_FEATURED_PRICE) score += 4;
    if (buyerDisplayPrice >= 1000) score += 1;
    if (buyerDisplayPrice >= 5000) score += 1;
    if (priceMin != null && buyerDisplayPrice >= priceMin) score += 1;
    if (priceMax != null && buyerDisplayPrice <= priceMax) score += 1;
    // bonus for being near the middle of the price range
    if (priceMin != null && priceMax != null && priceMax > priceMin) {
      const mid = (priceMin + priceMax) / 2;
      const delta = Math.abs(buyerDisplayPrice - mid);
      const spread = (priceMax - priceMin) || 1;
      score += Math.max(0, 3 - (delta / spread) * 3);
    }
  }
  // Followed artists boost
  if (product.vendor && followedArtists.length > 0) {
    if (followedArtists.map(a => a.toLowerCase()).includes(String(product.vendor).toLowerCase())) score += 8;
  }
  return score;
};

const applyFilters = (products, filters) => {
  const { style, medium, priceMin, priceMax, artist = '', editionType = '' } = filters || {};
  return products.filter(p => {
    if (style && style.trim() && p.tags.length > 0) {
      const s = style.trim().toLowerCase();
      const styleHit = p.tags.some(t => t.toLowerCase().includes(s));
      if (!styleHit) return false;
    }
    if (medium && medium.trim()) {
      if (!p.medium || String(p.medium).toLowerCase() !== medium.trim().toLowerCase()) return false;
    }
    if (artist && artist.trim()) {
      const a = artist.trim().toLowerCase();
      if (!p.vendor || !String(p.vendor).toLowerCase().includes(a)) return false;
    }
    if (editionType && editionType.trim()) {
      const et = editionType.trim().toLowerCase();
      const hasTag = p.tags.some(t => t.toLowerCase().includes(et));
      const hasField = (p.editionType && String(p.editionType).toLowerCase() === et) || (p._raw?.edition_type && String(p._raw.edition_type).toLowerCase() === et);
      if (!hasTag && !hasField) return false;
    }
    const buyerDisplayPrice = getMarketplaceBuyerPrice(p);
    if (priceMin != null && buyerDisplayPrice != null && buyerDisplayPrice < priceMin) return false;
    if (priceMax != null && buyerDisplayPrice != null && buyerDisplayPrice > priceMax) return false;
    return true;
  });
};

const sortProducts = (products, sort, prefs, aiBoostMap) => {
  const scored = products.map(p => {
    const base = computePreferenceScore(p, prefs);
    const aiBoost = aiBoostMap?.get(p.handle) || 0;
    return { p, score: base + aiBoost };
  });
  if (sort === 'price_low_high') {
    return scored.sort((a, b) => (getMarketplaceBuyerPrice(a.p) ?? Infinity) - (getMarketplaceBuyerPrice(b.p) ?? Infinity)).map(x => x.p);
  }
  if (sort === 'price_high_low') {
    return scored.sort((a, b) => (getMarketplaceBuyerPrice(b.p) ?? -Infinity) - (getMarketplaceBuyerPrice(a.p) ?? -Infinity)).map(x => x.p);
  }
  // default: preference score desc, tie-breaker price asc
  return scored
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const aPrice = getMarketplaceBuyerPrice(a.p) ?? 0;
      const bPrice = getMarketplaceBuyerPrice(b.p) ?? 0;
      const aAboveMinimum = aPrice >= MIN_FEATURED_PRICE ? 1 : 0;
      const bAboveMinimum = bPrice >= MIN_FEATURED_PRICE ? 1 : 0;
      if (bAboveMinimum !== aAboveMinimum) return bAboveMinimum - aAboveMinimum;
      return bPrice - aPrice;
    })
    .map(x => x.p);
};

const interleavePriceBuckets = (products) => {
  const bucketOrder = ['mid', 'accessible', 'upper_mid', 'premium', 'unknown', 'under_minimum'];
  const buckets = new Map(bucketOrder.map(bucket => [bucket, []]));

  products.forEach(product => {
    const bucket = getPriceBucket(product);
    if (!buckets.has(bucket)) buckets.set(bucket, []);
    buckets.get(bucket).push(product);
  });

  const result = [];
  while (Array.from(buckets.values()).some(items => items.length > 0)) {
    for (const bucket of bucketOrder) {
      const items = buckets.get(bucket);
      if (items?.length) {
        result.push(items.shift());
      }
    }
  }

  return result;
};

const diversifyAndCap = (products, itemsPerRow, perArtistCap, options = {}) => {
  const artistCounts = new Map();
  const filtered = [];
  const source = options.interleavePrices ? interleavePriceBuckets(products) : products;

  for (const prod of source) {
    const artist = prod.vendor || 'Unknown';
    const count = artistCounts.get(artist) || 0;
    if (count >= perArtistCap) continue;
    artistCounts.set(artist, count + 1);
    filtered.push(prod);
  }
  // Enforce max 1 per artist per row by interleaving groups
  const byArtist = new Map();
  filtered.forEach(p => {
    const key = p.vendor || 'Unknown';
    if (!byArtist.has(key)) byArtist.set(key, []);
    byArtist.get(key).push(p);
  });
  const queues = Array.from(byArtist.values()).sort((a, b) => b.length - a.length);
  const diversified = [];
  while (queues.some(q => q.length > 0)) {
    let rowCount = 0;
    for (let i = 0; i < queues.length && rowCount < itemsPerRow; i++) {
      const q = queues[i];
      if (q.length === 0) continue;
      const next = q.shift();
      diversified.push(next);
      rowCount += 1;
    }
  }
  return diversified;
};

const useCollectorPreferences = () => {
  const load = () => {
    try {
      const raw = localStorage.getItem(PREFS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  };
  const [prefs, setPrefs] = useState(load());
  const save = (next) => {
    setPrefs(next);
    try { localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
  };
  return [prefs, save];
};

const Marketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [aiResults, setAiResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const styleOptions = [
    'abstract', 'figurative', 'landscape', 'portrait', 'minimalism', 'surrealism', 'expressionism',
    'impressionism', 'realism', 'street', 'pop art', 'conceptual', 'geometric', 'naive', 'collage'
  ];
  const mediumOptions = [
    'oil', 'acrylic', 'watercolor', 'mixed_media', 'digital', 'charcoal', 'ink', 'pastel', 'gouache',
    'tempera', 'encaustic', 'collage', 'graphite', 'casein', 'alkyd', 'airbrush', 'dye', 'photography', 'sculpture', 'print'
  ];
  const editionOptions = [
    { value: '', label: 'Any' },
    { value: 'original', label: 'Original' },
    { value: 'limited', label: 'Limited Edition' },
    { value: 'open', label: 'Open Edition' },
    { value: 'artist_proof', label: 'Artist Proof' },
    { value: 'reproduction', label: 'Reproduction' }
  ];
  const priceRanges = [
    { key: 'any', label: 'Any', min: null, max: null },
    { key: 'under1k', label: 'Under $1k', min: null, max: 1000 },
    { key: '1to5', label: '$1k–$5k', min: 1000, max: 5000 },
    { key: '5to10', label: '$5k–$10k', min: 5000, max: 10000 },
    { key: '10plus', label: '$10k+', min: 10000, max: null }
  ];
  const urlMin = parseNumber(searchParams.get('min'), null);
  const urlMax = parseNumber(searchParams.get('max'), null);
  const initialCustomMin = urlMin ?? 0;
  const initialCustomMax = urlMax ?? 200000;
  const [customPrice, setCustomPrice] = useState([initialCustomMin, initialCustomMax]);

  const [filters, setFilters] = useState({
    style: searchParams.get('style') || '',
    medium: searchParams.get('medium') || '',
    priceMin: urlMin,
    priceMax: urlMax,
    artist: searchParams.get('artist') || '',
    editionType: searchParams.get('edition') || ''
  });
  const [sort, setSort] = useState(searchParams.get('sort') || 'relevance');
  const [page, setPage] = useState(parseNumber(searchParams.get('page'), 1) || 1);
  const [prefs, setPrefs] = useCollectorPreferences();
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Fetch artworks from database (primary source - has medium, dimensions, etc.)
  const fetchFromDatabase = async () => {
    try {
      const res = await api.get(`/explore/feed`, { params: { filter: 'artworks', search: '' } });
      const items = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.items) ? res.data.items : [];
      const mapped = items.map(it => normalizeProduct({
        id: it.id,
        title: it.title,
        handle: it.shopify_handle || it.handle,
        vendor: it.artist_name || it.author_name,
        medium: it.medium, // Now returned correctly from explore.js
        dimensions: it.dimensions,
        year_created: it.year_created,
        tags: it.tags || [],
        image: it.image_url,
        price: it.price,
        estimated_shipping_price: it.estimated_shipping_price,
        shipping_estimate_tier: it.shipping_estimate_tier,
        edition_type: it.edition_type
      }));
      return mapped;
    } catch (e) {
      console.error('Database fetch failed:', e);
      return [];
    }
  };

  // Fetch initial products - DATABASE FIRST (has accurate medium/dimensions)
  useEffect(() => {
    const fetchMarketplaceProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        // Primary source: Database (explore feed) - has accurate medium and dimensions
        const dbProducts = await fetchFromDatabase();
        
        if (dbProducts.length > 0) {
          setProducts(dbProducts);
        } else {
          // Fallback to Shopify if database is empty
          const response = await api.get('/shopify/products', { params: { ts: Date.now() } });
          const list = Array.isArray(response.data?.products) ? response.data.products : Array.isArray(response.data) ? response.data : [];
          const normalized = list.map(item => normalizeProduct({ ...item, price_includes_buyer_fees: true }));
          setProducts(normalized);
        }
      } catch (err) {
        console.error('Error fetching marketplace products:', err);
        setError('Failed to fetch marketplace products');
        setProducts([]);
      } finally {
        setLoading(false);
        setInitialLoaded(true);
      }
    };
    fetchMarketplaceProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const composeQueryFromFilters = () => {
    const parts = [];
    if (searchQuery && searchQuery.trim()) parts.push(searchQuery.trim());
    if (filters.medium) parts.push(filters.medium.replace(/_/g, ' '));
    if (filters.style) parts.push(filters.style);
    if (prefs.cities && prefs.cities[0]) parts.push(prefs.cities[0]);
    if (filters.artist) parts.push(filters.artist);
    if (filters.editionType) parts.push(filters.editionType.replace(/_/g, ' '));
    // Price is optional; only append if both bounds exist
    if (filters.priceMin != null || filters.priceMax != null) {
      const min = filters.priceMin != null ? `$${filters.priceMin}` : '$0';
      const max = filters.priceMax != null ? `$${filters.priceMax}` : '$200000+';
      parts.push(`${min} to ${max}`);
    }
    return parts.join(' ');
  };

  // Auto-run AI search when filters or query change (debounced)
  useEffect(() => {
    const q = composeQueryFromFilters();
    if (!q || !q.trim()) {
      // If no query context, clear AI results and show default grid
      setAiResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const aiResponse = await api.get(`/search/ai-search?query=${encodeURIComponent(q)}`).catch(() => ({ data: { results: [] } }));
        setAiResults(Array.isArray(aiResponse?.data?.results) ? aiResponse.data.results : []);
      } catch (_) {
        setAiResults([]);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters, prefs.cities]);

  // Run search when submitting
  const handleSearch = async (e) => {
    e.preventDefault();
    const q = composeQueryFromFilters();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const aiResponse = await api.get(`/search/ai-search?query=${encodeURIComponent(q)}`).catch(() => ({ data: { results: [] } }));
      setAiResults(Array.isArray(aiResponse?.data?.results) ? aiResponse.data.results : []);
      setPage(1);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('q', searchQuery || '');
        next.set('page', '1');
        return next;
      });
    } catch (err) {
      console.error('Error searching products:', err);
      setError('Failed to search products');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters, fallback broadening
  const filteredProducts = useMemo(() => {
    // Defensive: show only active/published items
    const activeOnly = products.filter(p => {
      const isSold = Boolean(p?.isSold || p?._raw?.is_sold);
      if (isSold) return false;

      const status = String(p?._raw?.status || p?.status || '').toLowerCase();
      const isPublished = Boolean(p?._raw?.published_at || p?._raw?.published || p?.status === 'published');
      // Accept if Shopify active or internal published; if no status present, keep it
      return status === 'active' || isPublished || status === '';
    });
    const searchScoped = activeOnly.filter(product => productMatchesSearch(product, searchQuery));
    let current = applyFilters(searchScoped, filters);
    // Gentle broadening (use activeOnly as base)
    if (current.length < 20) {
      const relaxed1 = { ...filters, style: '' };
      current = applyFilters(searchScoped, relaxed1);
    }
    if (current.length < 20) {
      const relaxed2 = { ...filters, style: '', medium: '' };
      current = applyFilters(searchScoped, relaxed2);
    }
    return current;
  }, [products, filters, searchQuery]);

  // Sort by preference/price and diversify
  const diversified = useMemo(() => {
    // Build AI boost map from AI results (scaled 0-10)
    const aiBoostMap = new Map();
    if (Array.isArray(aiResults) && aiResults.length > 0) {
      for (const r of aiResults) {
        const h = r?.handle || r?._raw?.handle;
        const s = typeof r?.score === 'number' ? r.score : 0;
        if (h) aiBoostMap.set(h, Math.max(0, Math.min(10, Math.round(s * 10))));
      }
    }
    const sorted = sortProducts(filteredProducts, sort, prefs, aiBoostMap);
    
    // If filtering by a specific artist, skip diversity cap to show all their works
    // Otherwise apply diversity logic to ensure variety
    if (filters.artist && filters.artist.trim()) {
      return sorted; // No cap when viewing a specific artist
    }
    
    return diversifyAndCap(sorted, DEFAULT_ITEMS_PER_ROW, DEFAULT_PER_ARTIST_CAP, {
      interleavePrices: sort === 'relevance'
    });
  }, [filteredProducts, sort, prefs, filters.artist, aiResults]);

  // Pagination
  const pageItems = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    const end = start + DEFAULT_PAGE_SIZE;
    return diversified.slice(start, end);
  }, [diversified, page]);

  const hasMore = diversified.length > page * DEFAULT_PAGE_SIZE;

  const extrasByArtist = useMemo(() => {
    const shownIds = new Set(pageItems.map(i => i.id));
    const extras = new Map();
    for (const p of diversified) {
      if (shownIds.has(p.id)) continue;
      const key = p.vendor || 'Unknown';
      if (!extras.has(key)) extras.set(key, []);
      const list = extras.get(key);
      if (list.length < 4) list.push(p);
    }
    return extras;
  }, [diversified, pageItems]);

  const updateParams = (next) => {
    setSearchParams(prev => {
      const sp = new URLSearchParams(prev);
      if (next.q !== undefined) sp.set('q', next.q);
      if (next.style !== undefined) sp.set('style', next.style || '');
      if (next.medium !== undefined) sp.set('medium', next.medium || '');
      if (next.priceMin !== undefined) next.priceMin != null ? sp.set('min', String(next.priceMin)) : sp.delete('min');
      if (next.priceMax !== undefined) next.priceMax != null ? sp.set('max', String(next.priceMax)) : sp.delete('max');
      if (next.artist !== undefined) sp.set('artist', next.artist || '');
      if (next.editionType !== undefined) sp.set('edition', next.editionType || '');
      if (next.sort !== undefined) sp.set('sort', next.sort || 'relevance');
      if (next.page !== undefined) sp.set('page', String(next.page || 1));
      return sp;
    });
  };

  // Auto-apply filters: sync URL when filters or sort change
  useEffect(() => {
    updateParams({
      q: searchQuery,
      style: filters.style,
      medium: filters.medium,
      priceMin: filters.priceMin,
      priceMax: filters.priceMax,
      artist: filters.artist,
      editionType: filters.editionType,
      sort,
      page: 1
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sort]);

  const handleClearFilters = () => {
    setFilters({ style: '', medium: '', priceMin: null, priceMax: null, artist: '', editionType: '' });
    setCustomPrice([0, 200000]); // Reset price sliders to default
    setPage(1);
    setSearchQuery('');
    setAiResults([]);
    setSearchParams(prev => {
      const sp = new URLSearchParams(prev);
      sp.delete('q');
      sp.delete('style');
      sp.delete('medium');
      sp.delete('min');
      sp.delete('max');
      sp.delete('artist');
      sp.delete('edition');
      sp.set('page', '1');
      return sp;
    });
  };

  const handleSavePreferences = () => {
    const next = {
      ...prefs,
      styles: filters.style ? [filters.style.toLowerCase()] : (prefs.styles || []),
      mediums: filters.medium ? [filters.medium.toLowerCase()] : (prefs.mediums || []),
      priceMin: filters.priceMin ?? prefs.priceMin ?? null,
      priceMax: filters.priceMax ?? prefs.priceMax ?? null
    };
    setPrefs(next);
  };

  return (
    <div className="marketplace-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="marketplace-header flex items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Art Marketplace</h1>
        <form onSubmit={handleSearch} className="search-form flex-1 max-w-lg flex gap-2">
          <input
            type="search"
            placeholder="Search artworks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(e); } }}
            className="search-input w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button type="submit" className="search-button inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition">Search</button>
        </form>
      </div>

      <div className="filters-bar mb-4">
        <form className="filters-form">
          <div className="filter-field">
            <select
              value={filters.style}
              onChange={(e) => setFilters(f => ({ ...f, style: e.target.value }))}
              className="filter-input rounded-md border border-gray-300 px-3 py-2"
              aria-label="Style"
            >
              <option value="">Style</option>
              {styleOptions.map(o => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
            </select>
          </div>
          <div className="filter-field">
            <select
              value={filters.medium}
              onChange={(e) => setFilters(f => ({ ...f, medium: e.target.value }))}
              className="filter-input rounded-md border border-gray-300 px-3 py-2"
              aria-label="Medium"
            >
              <option value="">Medium</option>
              {mediumOptions.map(o => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
            </select>
          </div>
          <div className="filter-field">
            <CityAutocomplete
              value={(prefs.cities && prefs.cities[0]) || ''}
              onChange={(city) => setPrefs({ ...prefs, cities: city ? [city] : [] })}
              placeholder="City (e.g., New York)"
              className="filter-input rounded-md border border-gray-300 px-3 py-2"
            />
          </div>
          <div className="filter-field">
            <input
              type="text"
              placeholder="Artist name"
              value={filters.artist}
              onChange={(e) => setFilters(f => ({ ...f, artist: e.target.value }))}
              className="filter-input rounded-md border border-gray-300 px-3 py-2"
              aria-label="Artist name"
            />
          </div>
          <div className="filter-field">
            <select
              value={filters.editionType}
              onChange={(e) => setFilters(f => ({ ...f, editionType: e.target.value }))}
              className="filter-input rounded-md border border-gray-300 px-3 py-2"
              aria-label="Edition type"
            >
              {editionOptions.map(o => <option key={o.value || 'any'} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="filters-toolbar">
            <div className="price-chips" role="group" aria-label="Price range">
              {priceRanges.map(r => {
                const active = (filters.priceMin ?? null) === r.min && (filters.priceMax ?? null) === r.max;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setFilters(f => ({ ...f, priceMin: r.min, priceMax: r.max }))}
                    className={`price-chip${active ? ' price-chip--active' : ''}`}
                    aria-pressed={active}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            <div className="price-custom">
              <span className="price-custom-label">Custom</span>
              <div className="price-sliders">
                <label className="price-slider">
                  <span className="visually-hidden">Minimum price</span>
                  <input
                    type="range"
                    min="0"
                    max="200000"
                    step="100"
                    value={customPrice[0] ?? 0}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      const next = [Math.min(v, customPrice[1]), customPrice[1]];
                      setCustomPrice(next);
                      setFilters(f => ({ ...f, priceMin: next[0], priceMax: next[1] }));
                    }}
                  />
                </label>
                <label className="price-slider">
                  <span className="visually-hidden">Maximum price</span>
                  <input
                    type="range"
                    min="0"
                    max="200000"
                    step="100"
                    value={customPrice[1] ?? 20000}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      const next = [customPrice[0], Math.max(v, customPrice[0])];
                      setCustomPrice(next);
                      setFilters(f => ({ ...f, priceMin: next[0], priceMax: next[1] }));
                    }}
                  />
                </label>
              </div>
              <span className="price-custom-value">${customPrice[0].toLocaleString()}–${customPrice[1].toLocaleString()}</span>
            </div>
            <div className="filter-actions">
              <select value={sort} onChange={(e) => { setSort(e.target.value); }} className="filter-select rounded-md border border-gray-300 px-2 py-2" aria-label="Sort">
                <option value="relevance">Relevance</option>
                <option value="price_low_high">Price: Low to High</option>
                <option value="price_high_low">Price: High to Low</option>
              </select>
              <button type="button" onClick={handleClearFilters} className="clear-button rounded-md border px-3 py-2 hover:bg-gray-50">Clear</button>
              <button type="button" onClick={handleSavePreferences} className="save-prefs-button rounded-md border px-3 py-2 hover:bg-gray-50">Save Prefs</button>
              <button type="button" onClick={() => { setSearchQuery(''); setAiResults([]); setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete('q'); n.set('page','1'); return n; }); }} className="rounded-md border px-3 py-2 hover:bg-gray-50">Clear Search</button>
            </div>
          </div>
        </form>
      </div>

      {error && (
        <div className="error-state rounded-md border border-red-200 bg-red-50 text-red-700 px-4 py-3 mb-4"><p>Error loading artworks: {error}</p></div>
      )}

      {loading && !initialLoaded ? (
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg border border-gray-200 overflow-hidden">
                <div className="h-60 bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="shopify-results-section">
            <div className="results-heading">
              <h2 className="text-xl font-semibold">All Artworks</h2>
              <div className="results-meta text-sm text-gray-500">
                {aiResults.length > 0 && <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-2 py-0.5 text-xs">Personalized by AI</span>}
                <span>{diversified.length.toLocaleString()} results</span>
              </div>
            </div>
            <div className="products-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
              {pageItems.length === 0 ? (
                <div className="empty-state col-span-full text-center text-gray-500 py-12">No products available.</div>
              ) : (
                pageItems.map((product) => {
                  const productHref = getShopifyProductUrl(product);
                  const imageUrl = product.image || PLACEHOLDER_IMG;
                  const displayPrice = formatMarketplaceBuyerPrice(product);
                  const editionLabel = product.editionType ? {
                    'original': 'Original',
                    'limited': 'Limited Edition',
                    'open': 'Open Edition',
                    'artist_proof': 'Artist Proof',
                    'reproduction': 'Print'
                  }[product.editionType] || product.editionType : null;
                  
                  // Use medium field (not productType which is "Artwork")
                  const mediumDisplay = product.medium ? String(product.medium).replace(/_/g, ' ') : null;
                  const dimensionsDisplay = formatDimensions(product.dimensions);
                  
                  const Card = (
                    <div className="product-card group rounded-xl border border-gray-100 overflow-hidden bg-white transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                      {/* Image container - centered artwork, no cropping */}
                      <div className="artwork-image-container">
                        <img
                          src={imageUrl}
                          alt={product.title || 'Artwork'}
                          onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                          className="product-image transition-transform duration-500 group-hover:scale-105"
                        />
                        {/* Edition badge */}
                        {editionLabel && (
                          <span className="absolute top-3 left-3 px-2 py-1 text-[10px] font-medium uppercase tracking-wide bg-white/90 backdrop-blur-sm text-gray-700 rounded-full shadow-sm">
                            {editionLabel}
                          </span>
                        )}
                      </div>
                      
                      {/* Card content */}
                      <div className="product-info p-4">
                        {/* Title and Artist */}
                        <h3 className="product-title text-sm font-semibold text-gray-900 line-clamp-1 mb-1">
                          {product.title || 'Untitled'}
                        </h3>
                        {product.vendor && (
                          <p className="product-artist text-xs text-gray-500 mb-2">{product.vendor}</p>
                        )}
                        
                        {/* Medium and Dimensions row - darker text for readability */}
                        {(mediumDisplay || dimensionsDisplay) && (
                          <div className="flex items-center gap-2 text-[11px] text-gray-600 mb-3">
                            {mediumDisplay && (
                              <span className="capitalize">{mediumDisplay}</span>
                            )}
                            {mediumDisplay && dimensionsDisplay && (
                              <span className="text-gray-400">•</span>
                            )}
                            {dimensionsDisplay && (
                              <span>{dimensionsDisplay}</span>
                            )}
                          </div>
                        )}
                        
                        {/* Price - prominent display */}
                        {displayPrice && (
                          <p className="product-price text-base font-bold text-gray-900">
                            {displayPrice}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                  if (productHref) {
                    return (
                      <a
                        href={productHref}
                        target="_top"
                        onClickCapture={navigateToStorefrontProduct(productHref)}
                        key={product.id || product.handle || productHref}
                        className="block"
                      >
                        {Card}
                      </a>
                    );
                  }
                  return <div key={product.id || Math.random()} className="cursor-default opacity-90">{Card}</div>;
                })
              )}
            </div>
            {hasMore && (
              <div className="pagination-controls flex justify-center mt-6">
                <button
                  className="load-more-button inline-flex items-center rounded-md border px-4 py-2 hover:bg-gray-50"
                  onClick={() => { const next = page + 1; setPage(next); updateParams({ page: next }); }}
                >
                  Load more
                </button>
              </div>
            )}
          </div>

          {extrasByArtist.size > 0 && (
            <div className="more-from-artists mt-10">
              <h2 className="text-xl font-semibold mb-2">More from these artists</h2>
              <div className="artists-extras grid grid-cols-1 md:grid-cols-2 gap-6">
                {Array.from(extrasByArtist.entries()).map(([artist, items]) => (
                  <div className="artist-extras-block" key={artist}>
                    <h3 className="font-semibold mb-2">{artist}</h3>
                    <div className="products-grid small-grid grid grid-cols-2 sm:grid-cols-3 gap-4">
                      {items.map(item => {
                        const imageUrl = item.image || PLACEHOLDER_IMG;
                        const productHref = getShopifyProductUrl(item);
                        const itemMedium = item.medium ? String(item.medium).replace(/_/g, ' ') : null;
                        const itemDims = formatDimensions(item.dimensions);
                        const SmallCard = (
                          <div className="product-card small group rounded-xl border border-gray-100 overflow-hidden bg-white transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
                            {/* Centered artwork, no cropping */}
                            <div className="artwork-image-container">
                              <img
                                src={imageUrl}
                                alt={item.title || 'Artwork'}
                                onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_IMG; }}
                                className="product-image transition-transform duration-300 group-hover:scale-105"
                              />
                            </div>
                            <div className="product-info p-3">
                              <h4 className="product-title text-xs font-semibold text-gray-900 line-clamp-1 mb-1">{item.title || 'Untitled'}</h4>
                              {(itemMedium || itemDims) && (
                                <p className="text-[10px] text-gray-600 mb-1 line-clamp-1">
                                  {itemMedium}{itemMedium && itemDims ? ' • ' : ''}{itemDims}
                                </p>
                              )}
                              {formatMarketplaceBuyerPrice(item) && (
                                <p className="product-price text-sm font-bold text-gray-900">{formatMarketplaceBuyerPrice(item)}</p>
                              )}
                            </div>
                          </div>
                        );
                        if (productHref) {
                          return (
                            <a
                              href={productHref}
                              target="_top"
                              onClickCapture={navigateToStorefrontProduct(productHref)}
                              key={item.id || item.handle || productHref}
                              className="block"
                            >
                              {SmallCard}
                            </a>
                          );
                        }
                        return <div key={item.id || Math.random()} className="cursor-default opacity-90">{SmallCard}</div>;
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Marketplace;